<template>
  <div class="space-y-4">
    <span class="text-headline">
      {{ $t("COMMON.CHOOSE") }} {{ $t("COMMON.CONTACT") }}
    </span>
    <div class="space-y-2 rounded-sm border p-4">
      <lf-checkbox
        v-if="currentUser?.email"
        v-model="selectedUsersIds"
        :value="currentUser.id"
        name="current_user"
      >
        <div class="flex flex-col space-y-1">
          <span class="text-headline">
            {{
              type === CommunicationType.email
                ? $t("ORGANIZATION.COMMUNICATION_TEMPLATES.MY_EMAIL")
                : $t("ORGANIZATION.COMMUNICATION_TEMPLATES.MY_PHONE")
            }}
          </span>
          <span class="text-label">
            {{
              type === CommunicationType.email
                ? currentUser.email
                : currentUser.phone
            }}
          </span>
        </div>
      </lf-checkbox>
      <div v-if="contacts?.length" class="flex flex-col space-y-2">
        <lf-checkbox
          v-model="organizationContacts"
          value="organization_contacts"
          name="organization_contacts"
        >
          <div class="flex flex-col space-y-1">
            <span class="text-headline">
              {{
                $t("ORGANIZATION.COMMUNICATION_TEMPLATES.ORGANIZATION_CONTACTS")
              }}
            </span>
            <span class="text-label">
              {{
                $t(
                  "ORGANIZATION.COMMUNICATION_TEMPLATES.FROM_YOUR_ORGANIZATION"
                )
              }}
            </span>
          </div>
        </lf-checkbox>
        <div v-if="organizationContacts" class="space-y-1">
          <search-input
            v-model.trim="searchText"
            no-margin
            no-padding
            data-cy="search-input"
          />
          <div class="max-h-60 space-y-1 overflow-y-auto">
            <label
              v-for="user in filteredUsers"
              :key="user.id"
              :data-cy="`user-${user.id}`"
              class="flex cursor-pointer items-center justify-between rounded-sm p-2 hover:bg-primary-background-active"
              :class="{
                'bg-primary-background-active': selectedUsersIds?.includes(
                  user.id
                ),
              }"
            >
              <input
                v-model="selectedUsersIds"
                class="hidden"
                type="checkbox"
                :value="user.id"
              />
              <div class="flex flex-col space-y-1">
                <span class="text-headline">{{ getUserFullName(user) }}</span>
                <span class="text-label">
                  {{ checkIsEmail(user) ? user.email : user.phone }}
                </span>
              </div>
              <icon-base
                v-if="selectedUsersIds?.includes(user.id)"
                :icon="IconCheck"
                :icon-color="UTIL_COLORS.PRIMARY"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import { CommunicationType } from "@/enums/communicationLogs";
import { UTIL_COLORS } from "@/helpers/constants";

import IconCheck from "@/components/icons/IconCheck.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

import type { EmailTestUser, TestEmailData } from "@/models/emails";
import type { SmsTestUser, TestSmsData } from "@/models/sms";
import type { IUser } from "@/models/users";

const props = defineProps<{
  contacts: TestEmailData["contacts"] | TestSmsData["contacts"];
  type: "email" | "sms";
}>();

const selectedUsersIds = defineModel<number[]>({ default: [] });

const { getters } = useStore();

const organizationContacts = ref(false);
const searchText = ref("");

const filteredUsers = computed<
  TestEmailData["contacts"] | TestSmsData["contacts"]
>(() => {
  if (!searchText.value) {
    return props.contacts || [];
  }

  return (
    props.contacts?.filter((contact) => {
      const { first_name, last_name } = contact;
      const rest = checkIsEmail(contact) ? contact.email : contact.phone;
      `${last_name}${first_name}${rest}`
        .toLowerCase()
        .includes(searchText.value.toLowerCase());
    }) || []
  );
});

const currentUser = computed<IUser>(() => getters["auth/user"]);

const getUserFullName = ({
  first_name,
  last_name,
}: {
  first_name: string;
  last_name: string;
}) => [first_name, last_name].filter(Boolean).join(" ");

const checkIsEmail = (
  model: SmsTestUser | EmailTestUser
): model is EmailTestUser => {
  return "email" in model;
};
</script>
